import { useEffect, useRef } from 'react';
import { CircularProgress, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { InvoiceStatus, useInvoiceStatusQuery } from '../../../../api/gql';
import { useSafeNavigation } from '../../../../hooks/useSafeNavigation';
import { StripePaymentResultNotificator } from '../../../../modules/payment/PaymentResultNotificator';
import { getInvoiceStateProperties } from '../../../../utils/invoice';

export const InvoicePaymentResult = () => {
  const { invoiceId } = useParams();
  const { t } = useTranslation('payment');
  const { safeNavigate } = useSafeNavigation();
  const isMounted = useRef(true);

  const { data, isFetching } = useInvoiceStatusQuery(
    {
      id: invoiceId ?? '',
    },
    {
      refetchInterval: 1000,
      enabled: isMounted.current,
    },
  );

  useEffect(() => {
    // Set mounted flag
    isMounted.current = true;

    // Cleanup function
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!data || isFetching || !isMounted.current) {
      return;
    }

    if (data.invoice.status === InvoiceStatus.AwaitingPayment) {
      return;
    }

    // Make sure component is still mounted before navigating
    if (isMounted.current) {
      safeNavigate(getInvoiceStateProperties(data.invoice.status, data.invoice.id).route, { replace: true });
    }
  }, [data, isFetching, safeNavigate]);

  return (
    <Dialog open={true} aria-labelledby="modal-title">
      <DialogTitle id={'modal-title'} align={'left'}>
        {t('processingPayment')}
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          display: 'flex',
          p: 4,
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <CircularProgress />
        <Typography>{t('afterProcessingInfo')}</Typography>
      </DialogContent>
      <StripePaymentResultNotificator onError={() => safeNavigate('..')} />
    </Dialog>
  );
};
